import type { JSX, ReactNode } from 'react';
import { Check } from 'lucide-react';
import { PlatformIcon } from '@/components/PlatformIcon';
import { Pill } from '@/components/ui/pill';
import { cn } from '@/lib/utils';
import { STATUS_TONES, statusText } from '@/lib/bulk-import/copy';
import type { ImportRow, RowCheck, RowStatus } from '@/lib/bulk-import/rows';

export type WizardStep = 'upload' | 'columns' | 'review' | 'action' | 'results';

export const WIZARD_STEPS: ReadonlyArray<{ key: WizardStep; label: string }> = [
  { key: 'upload', label: 'Upload' },
  { key: 'columns', label: 'Columns' },
  { key: 'review', label: 'Review' },
  { key: 'action', label: 'Do it' },
  { key: 'results', label: 'Results' },
];

/** Numbered steps across the top. Earlier steps are links back when `onPick` allows it. */
export function StepIndicator({
  current,
  onPick,
}: {
  current: WizardStep;
  onPick?: (step: WizardStep) => void;
}): JSX.Element {
  const at = WIZARD_STEPS.findIndex((s) => s.key === current);
  return (
    <ol className="flex flex-wrap items-center gap-x-1 gap-y-2 text-sm" aria-label="Import steps">
      {WIZARD_STEPS.map((s, i) => {
        const done = i < at;
        const active = i === at;
        const clickable = done && onPick;
        const inner = (
          <>
            <span
              className={cn(
                'grid h-6 w-6 shrink-0 place-items-center rounded-full border text-xs font-semibold tabular-nums',
                active && 'border-brand/40 bg-brand/15 text-brand',
                done && 'border-success/30 bg-success/10 text-success-strong',
                !active && !done && 'border-border text-muted-foreground',
              )}
            >
              {done ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> : i + 1}
            </span>
            <span className={cn(active ? 'font-semibold text-foreground' : 'text-muted-foreground')}>{s.label}</span>
          </>
        );
        return (
          <li key={s.key} className="flex items-center gap-1" aria-current={active ? 'step' : undefined}>
            {clickable ? (
              <button
                type="button"
                onClick={() => onPick(s.key)}
                className="flex items-center gap-2 rounded-lg px-1.5 py-1 transition-colors hover:bg-accent"
              >
                {inner}
              </button>
            ) : (
              <span className="flex items-center gap-2 px-1.5 py-1">{inner}</span>
            )}
            {i < WIZARD_STEPS.length - 1 && <span className="h-px w-4 bg-border sm:w-8" aria-hidden />}
          </li>
        );
      })}
    </ol>
  );
}

/** Panel with a title row, for each step's content. */
export function StepPanel({
  title,
  description,
  actions,
  children,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <section className={cn('panel space-y-4 rounded-xl p-4 sm:p-6', className)}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h2 className="text-[15px] font-bold">{title}</h2>
          {description && <p className="max-w-[70ch] text-sm text-muted-foreground">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

export function HandleCell({ row }: { row: ImportRow }): JSX.Element {
  if (!row.username) {
    return (
      <span className="block max-w-[32ch] truncate text-muted-foreground" title={row.rawHandle}>
        {row.rawHandle || '—'}
      </span>
    );
  }
  return (
    <span className="flex min-w-0 items-center gap-2">
      <PlatformIcon platform={row.channel} size={14} className="shrink-0 text-muted-foreground" />
      <span className="truncate" title={row.rawHandle}>
        @{row.username}
      </span>
    </span>
  );
}

export function RowStatusPill({
  status,
  check,
  row,
  duplicateRow,
}: {
  status: RowStatus;
  check?: RowCheck;
  row: ImportRow;
  duplicateRow?: number;
}): JSX.Element {
  const text = statusText(status, check, { error: row.error, rowNumber: row.rowNumber, duplicateRow });
  return (
    <Pill tone={STATUS_TONES[status]} title={text} className="max-w-[34ch]">
      <span className="truncate">{text}</span>
    </Pill>
  );
}

/** Thin progress bar with a label. */
export function ProgressBar({ done, total, label }: { done: number; total: number; label: string }): JSX.Element {
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{label}</span>
        <span className="font-mono tabular-nums">
          {done.toLocaleString('en-US')} / {total.toLocaleString('en-US')}
        </span>
      </div>
      <div
        className="h-2 overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
      >
        <div className="h-full rounded-full bg-brand transition-[width] duration-200 ease-out-quart" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export const plural = (n: number, one: string, many = `${one}s`): string =>
  `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;
